// src/pages/ProductManagement/EnglishProductManagement.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Form, Button, Modal, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faExclamationTriangle, faPlus } from '@fortawesome/free-solid-svg-icons';
import ReactPaginate from 'react-paginate';
import axios from '../../utils/axiosConfig';
import './ProductManagement.css';

const EnglishProductManagement = () => {
  const [englishProducts, setEnglishProducts] = useState([]);
  const [turkishProducts, setTurkishProducts] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState({ type: '', message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('title');
  const [currentPage, setCurrentPage] = useState(0);
  const [deleteType, setDeleteType] = useState(''); // 'english' or 'turkish'
  const itemsPerPage = 10;

  useEffect(() => {
    fetchEnglishProducts();
    fetchTurkishProducts();
    // eslint-disable-next-line
  }, []);

  const fetchEnglishProducts = async () => {
    try {
      const response = await axios.get('/api/english-products');
      setEnglishProducts(response.data);
    } catch (error) {
      console.error('Error fetching English products:', error);
      setFeedbackMessage({ type: 'danger', message: 'Error fetching English products.' });
    }
  };

  const fetchTurkishProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setTurkishProducts(response.data);
    } catch (error) {
      console.error('Error fetching Turkish products:', error);
      setFeedbackMessage({ type: 'danger', message: 'Error fetching Turkish products.' });
    }
  };

  const handleDeleteClick = (product, type) => {
    setProductToDelete(product);
    setDeleteType(type);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      if (deleteType === 'english') {
        await axios.delete(`/api/english-products/${productToDelete._id}`);
        setEnglishProducts(englishProducts.filter((p) => p._id !== productToDelete._id));
      } else {
        await axios.delete(`/api/products/${productToDelete._id}`);
        setTurkishProducts(turkishProducts.filter((p) => p._id !== productToDelete._id));
      }
      
      setFeedbackMessage({ 
        type: 'success', 
        message: `${productToDelete.title} successfully deleted.` 
      });
    } catch (error) {
      console.error('Error deleting product:', error);
      setFeedbackMessage({ 
        type: 'danger', 
        message: 'Error deleting product. Please try again.' 
      });
    } finally {
      setShowConfirmModal(false);
      setProductToDelete(null);
      setDeleteType('');
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setProductToDelete(null);
    setDeleteType('');
  };

  // Combine all products for display
  const getAllProducts = () => {
    const englishWithType = englishProducts.map(product => ({
      ...product,
      type: 'english',
      displayTitle: product.title
    }));
    
    const turkishWithType = turkishProducts
      .filter(product => product.hasEnglishTranslation)
      .map(product => ({
        ...product,
        type: 'turkish',
        displayTitle: product.title
      }));
    
    return [...englishWithType, ...turkishWithType];
  };

  // Search Functionality
  const filteredProducts = getAllProducts().filter(
    (product) =>
      product.displayTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sorting Functionality
  const sortedProducts = React.useMemo(() => {
    let sortableProducts = [...filteredProducts];
    if (sortKey !== null) {
      sortableProducts.sort((a, b) => {
        if (a[sortKey] < b[sortKey]) {
          return -1;
        }
        if (a[sortKey] > b[sortKey]) {
          return 1;
        }
        return 0;
      });
    }
    return sortableProducts;
  }, [filteredProducts, sortKey]);

  // Pagination Logic
  const pageCount = Math.ceil(sortedProducts.length / itemsPerPage);
  const displayProducts = sortedProducts.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  return (
    <div className="product-management">
      <h1>English Product Management</h1>
      <p className="text-muted mb-4">
        Manage English products and existing Turkish products with English translations
      </p>

      {/* Add Product Button */}
      <div className="add-product-button-container">
        <Link to="/add-english-product">
          <Button variant="primary" className="add-product-button">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add New English Product
          </Button>
        </Link>
      </div>

      {/* Feedback Message */}
      {feedbackMessage.message && (
        <div className={`alert alert-${feedbackMessage.type} mt-3`} role="alert">
          {feedbackMessage.message}
        </div>
      )}

      {/* Search and Sort Controls */}
      <Form className="mb-4">
        <Row>
          <Col md={6} className="mb-2">
            <Form.Control
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search Products"
            />
          </Col>
          <Col md={6} className="mb-2">
            <Form.Select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label="Sort Products"
            >
              <option value="title">Sort by Title</option>
              <option value="category">Sort by Category</option>
              <option value="createdAt">Sort by Date Added</option>
            </Form.Select>
          </Col>
        </Row>
      </Form>

      {/* Product List */}
      {displayProducts.length === 0 ? (
        <div className="text-center mt-5">
          <h4>No products found</h4>
          <p className="text-muted">
            {searchQuery ? 'No products match your search criteria.' : 'No products available.'}
          </p>
          {!searchQuery && getAllProducts().length === 0 && (
            <Link to="/add-english-product">
              <Button variant="primary" size="lg">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add First English Product
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <>
          <ul className="product-list">
            {displayProducts.map((product) => (
              <li key={product._id} className="product-item">
                <div className="product-info">
                  <span className="product-title">{product.displayTitle}</span>
                  <span className={`badge ${product.type === 'english' ? 'bg-success' : 'bg-info'} ms-2`}>
                    {product.type === 'english' ? 'New English' : 'Translated'}
                  </span>
                </div>
                <div className="product-actions">
                  <button
                    onClick={() => handleDeleteClick(product, product.type)}
                    className="product-delete-btn"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Pagination Controls */}
      {pageCount > 1 && (
        <ReactPaginate
          previousLabel={'← Previous'}
          nextLabel={'Next →'}
          pageCount={pageCount}
          onPageChange={handlePageClick}
          containerClassName={'pagination'}
          pageClassName={'page-item'}
          pageLinkClassName={'pagination__link'}
          previousClassName={'page-item'}
          previousLinkClassName={'pagination__link'}
          nextClassName={'page-item'}
          nextLinkClassName={'pagination__link'}
          activeClassName={'active'}
          activeLinkClassName={'pagination__link--active'}
          disabledClassName={'disabled'}
          disabledLinkClassName={'pagination__link--disabled'}
        />
      )}

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={handleCancelDelete} centered className="confirmation-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} color="#ffc107" /> Delete Product
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {productToDelete && (
            <p>
              Are you sure you want to delete the {deleteType === 'english' ? 'English' : 'existing'} product <strong>{productToDelete.displayTitle || productToDelete.title}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EnglishProductManagement;
